/* eslint-disable react/no-unstable-nested-components */
import { Card, Stack } from "@mui/material";
import { DataGrid, gridStringOrNumberComparator } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import CircularProgress from "@mui/material/CircularProgress";
import { getAPICall, deleteAPICall } from "lib/callApi";
import {
  GET_COMPANIES_ENABLED_MODULES,
  GET_COMPANY_AND_MODULE_LISTS,
  COMPANY_ENABLED_MODULE,
} from "lib/constants";
import { enqueueSnackbar } from "notistack";
import "styles/snapshot.scss";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ModuleManagementModal from "./ModuleManagementModal";

function ModulesManagement() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [companiesModulesData, setCompaniesModulesData] = useState([]);
  const [moduleManagementModal, setModuleManagementModal] = useState(false);

  const [companiesList, setCompaniesList] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [editFormValues, setEditFormValues] = useState();

  const getModulesData = async () => {
    setIsLoading(true);
    try {
      const result = await getAPICall(GET_COMPANIES_ENABLED_MODULES, true);
      if (result?.data?.success) {
        setCompaniesModulesData(result?.data?.modules || []);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not retrieve companies modules data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text:
            error?.response?.data?.message ||
            error.message ||
            "Could not retrieve companies modules data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getCompanyAndModuleLists = async () => {
    setIsLoading(true);
    try {
      const result = await getAPICall(GET_COMPANY_AND_MODULE_LISTS, true);
      if (result?.data?.success) {
        setCompaniesList(result?.data?.data?.companies || []);
        setModulesList(result?.data?.data?.modules || []);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not retrieve companies modules data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text:
            error?.response?.data?.message ||
            error.message ||
            "Could not retrieve companies modules data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (data) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${data?.company?.name}-${data?.Module?.module_name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, delete it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const resultResponse = await deleteAPICall(`${COMPANY_ENABLED_MODULE}/${data.id}`, true);
          try {
            if (resultResponse?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: "Module successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );
              getModulesData();
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: resultResponse?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch((error) => {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: error.response.data.message,
          }),
          {
            variant: "custom",
          }
        );
      });
  };

  function isNullOrEmpty(str) {
    return str === null || str === "";
  }

  // Custom sorter than puts null/empty values at the bottom of the sort
  const nullHandlingSortComparator = (v1, v2, cellParams1, cellParams2) => {
    if (isNullOrEmpty(v1) || isNullOrEmpty(v2)) {
      if (v1 === v2) return 0;
      const sortModel = cellParams1.api.getSortModel();
      const sortColumn = sortModel.find((sm) => sm.field === cellParams1.field);
      if (sortColumn && sortColumn.sort === "desc") {
        return isNullOrEmpty(v1) ? -1 : 1;
      }
      return isNullOrEmpty(v1) ? 1 : -1;
    }
    return gridStringOrNumberComparator(v1, v2, cellParams1, cellParams2);
  };

  const columns = [
    {
      field: "company",
      headerName: "Company",
      headerClassName: "datatable-header",
      minWidth: 250,
      flex: 1,
      valueGetter: (company) => company.name,
    },
    {
      field: "Module",
      headerName: "Module",
      headerClassName: "datatable-header",
      minWidth: 250,
      flex: 1,
      valueGetter: (Module) => Module?.module_name,
    },
    {
      field: "ModulesComponent",
      headerName: "Module Component",
      headerClassName: "datatable-header",
      minWidth: 250,
      flex: 1,
      valueGetter: (ModulesComponent) => ModulesComponent?.component_name,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "enabled",
      headerName: "Enabled",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      disableSortBy: true,
      renderCell: (params) => (
        <MDBox sx={{ display: "flex" }}>
          <MDBox>
            <MDBadge
              variant="contained"
              color={params.value ? "success" : "error"}
              badgeContent={params.value ? "True" : "False"}
              container
            />
          </MDBox>
        </MDBox>
      ),
    },
    {
      field: "repeatInterval",
      headerName: "",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      disableSortBy: true,
      renderCell: (params) => (
        <MDBox sx={{ fontSize: "20px", display: "flex" }}>
          <EditIcon
            style={{ marginRight: "15px", cursor: "pointer" }}
            onClick={() => {
              setEditFormValues(params.row);
              setModuleManagementModal(true);
            }}
          />
          <DeleteIcon style={{ cursor: "pointer" }} onClick={() => handleDelete(params.row)} />
        </MDBox>
      ),
    },
  ];

  useEffect(() => {
    const userData = localStorage.getItem("user");

    if (!userData) {
      navigate("/sign-in");
    }

    getModulesData();
    getCompanyAndModuleLists();
  }, []);

  const handleCloseModal = () => {
    setModuleManagementModal(false);
    setEditFormValues();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox
        pr={1}
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          "& .MuiInputLabel-formControl": { lineHeight: "15px" },
        }}
        mt={2}
      >
        <MDButton
          color="primaryRed"
          onClick={() => {
            setModuleManagementModal(true);
            setEditFormValues();
          }}
        >
          + Add New
        </MDButton>
      </MDBox>

      <div role="tabpanel" id={`simple-tabpanel-${0}`} aria-labelledby={`simple-tab-${0}`}>
        <MDBox sx={{ pt: 3 }}>
          <Card>
            <MDBox p={3} pb={0} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Alysio Modules Management
              </MDTypography>
            </MDBox>

            <MDBox
              sx={{
                width: "100%",
                overflowX: "auto",
                fontSize: "0.875rem",
              }}
            >
              {isLoading ? (
                <Stack alignItems="center">
                  <CircularProgress
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                    }}
                    color="info"
                    size="10vh"
                  />
                </Stack>
              ) : (
                ""
              )}

              <DataGrid
                sx={{
                  mt: "1rem",
                  "&.MuiTypography-root": {
                    fontSize: "1rem !important",
                  },
                }}
                rows={companiesModulesData}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </MDBox>
          </Card>
        </MDBox>
      </div>

      {moduleManagementModal ? (
        <ModuleManagementModal
          open={moduleManagementModal}
          handleModalClose={handleCloseModal}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          companiesList={companiesList}
          modulesList={modulesList}
          editFormValues={editFormValues}
          getModulesData={getModulesData}
        />
      ) : null}
    </DashboardLayout>
  );
}

export default ModulesManagement;
